'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

export function useStickyHeader() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement | null>(null);

  const headerCallbackRef = useCallback((node: HTMLDivElement | null) => {
    headerRef.current = node;
    if (node) {
      setHeaderHeight(node.offsetHeight);
    }
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setIsScrolled(container.scrollTop > 10);
          ticking = false;
        });
        ticking = true;
      }
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const header = headerRef.current;
    if (!header) {
      return;
    }

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const height
          = entry.borderBoxSize?.[0]?.blockSize ?? (entry.target as HTMLElement).offsetHeight;
        setHeaderHeight(height);
      }
    });

    resizeObserver.observe(header);
    return () => resizeObserver.disconnect();
  }, []);

  return { containerRef, headerCallbackRef, isScrolled, headerHeight };
}
